// Healthie's own links, removed from a message body before it is rendered.
//
// ⛔ WHY THEY MUST GO. When a provider uses the dashboard's "Attach file", Healthie appends an anchor
// to the message body pointing at `https://<host>.gethealthie.com/documents/<id>/download`. It is a
// durable URL, and it is useless to a member: it redirects to a HEALTHIE LOGIN PAGE, and members
// have no Healthie account. Left in place, the bubble shows two links to the same file — ours, which
// works, beside one that dead-ends on a login form. "Carrot is broken" is what that reads as.
//
// ⚠ IT LOOKS FINE TO EVERY AUTOMATED CHECK. The endpoint answers HTTP 200 with a client-side app
// shell and decides authentication in the browser, so `curl` and any status-code assertion read it
// as working. Only a real browser shows the login page. That is why this is removed by rule rather
// than left for someone to notice.
//
// HOST-MATCHED, NOT PATH-MATCHED, and that is the wider rule on purpose: every gethealthie.com URL
// is a dead end for a member, not just `/documents/<id>/download`. A prose link a provider typed to
// some other Healthie page would fail the same way, so dropping it is right for the same reason —
// and a path-matched rule would silently start showing dead links again the day Healthie changes
// that path.

/** Healthie's own domain. Subdomains included — `securestaging.` on stage, `secure.` in production. */
const HEALTHIE_DOMAIN = "gethealthie.com"

/**
 * Is this href a link into Healthie's own web app?
 *
 * ⚠ PARSED, NOT PATTERN-MATCHED. A substring test for the domain would also fire on
 * `https://example.invalid/?next=gethealthie.com` (not a Healthie link) and — worse — could be
 * dodged by anything that puts the real host somewhere a naive match does not look. Parsing asks the
 * only question that matters: what host would the browser actually go to?
 *
 * A relative or unparseable href is NOT Healthie's: it cannot be, since Healthie's injected anchors
 * are always absolute, and guessing about a malformed one risks removing a link a provider meant.
 */
export function isHealthieLink(href: string | null | undefined): boolean {
  if (!href) return false
  let host: string
  try {
    host = new URL(href).hostname.toLowerCase()
  } catch {
    return false
  }
  return host === HEALTHIE_DOMAIN || host.endsWith(`.${HEALTHIE_DOMAIN}`)
}

/**
 * The same body with Healthie's own links removed — anchor and link text together.
 *
 * ⚠ THE TEXT GOES WITH THE ANCHOR, deliberately. Healthie sets the link text to the raw URL, so
 * unwrapping instead of removing would leave `https://securestaging.gethealthie.com/documents/…`
 * sitting in the middle of the member's message as plain text. There is nothing to preserve.
 *
 * ⚠ DOM-BASED, so it cannot mangle the rest of the body. This runs on a string that goes on to be
 * sanitized and parsed for rendering anyway; a regex over anchors would be the thing that eventually
 * eats a `>` in someone's message text.
 *
 * Returns the input unchanged when there is nothing to remove — which is the overwhelmingly common
 * case, and keeps the object identity stable for a body nobody has to touch.
 */
export function stripHealthieLinks(body: string): string {
  // Cheap guard first: the parse and re-serialize below is pointless for a body with no anchor at
  // all, which is most of them.
  //
  // ⚠ CASE-INSENSITIVE, AND `\b`-ANCHORED. The strip below is case-blind already — `innerHTML`
  // lowercases tag names while parsing, so `querySelectorAll("a")` finds `<A HREF=…>` — which made
  // a case-SENSITIVE prefilter the one thing that could let an uppercase Healthie anchor through,
  // and this function's contract is that EVERY gethealthie.com url goes. `\b` also stops it firing
  // on `<abbr`/`<address`/`<article`, which the old substring test matched and paid a full parse for.
  if (!/<a\b/i.test(body)) return body

  const container = document.createElement("div")
  container.innerHTML = body

  const healthieAnchors = [...container.querySelectorAll("a")].filter((anchor) =>
    isHealthieLink(anchor.getAttribute("href"))
  )
  if (healthieAnchors.length === 0) return body

  for (const anchor of healthieAnchors) {
    anchor.remove()
  }
  return container.innerHTML
}
